import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, combineLatest, from, mergeMap, of } from 'rxjs';
import { ExcludedUrlRegex } from '../interfaces/keycloak-options';
import { KeycloakService } from '../services/keycloak.service';

export const keycloakBearerInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const keycloak = inject(KeycloakService);
  const { enableBearerInterceptor, excludedUrls } = keycloak;
  if (!enableBearerInterceptor) {
    return next(req);
  }

  const shallPass: boolean =
    !keycloak.shouldAddToken(req) ||
    excludedUrls.findIndex((item) => isUrlExcluded(req, item)) > -1;
  if (shallPass) {
    return next(req);
  }

  return combineLatest([
    from(conditionallyUpdateToken(req, keycloak)),
    of(keycloak.isLoggedIn()),
  ]).pipe(
    mergeMap(([, isLoggedIn]) =>
      isLoggedIn
        ? handleRequestWithTokenHeader(req, next, keycloak)
        : from(keycloak.login({ prompt: 'none' })).pipe(
            mergeMap(() => handleRequestWithTokenHeader(req, next, keycloak)),
          ),
    ),
  );
};

/**
 * @deprecated
 * Checks if the url is excluded from having the Bearer Authorization
 * header added.
 *
 * @param req http request from @angular http module.
 * @param excludedUrlRegex contains the url pattern and the http methods,
 * excluded from adding the bearer at the Http Request.
 */
function isUrlExcluded(
  { method, url }: HttpRequest<unknown>,
  { urlPattern, httpMethods }: ExcludedUrlRegex,
): boolean {
  const httpTest =
    !httpMethods ||
    httpMethods.length === 0 ||
    httpMethods.join().indexOf(method.toUpperCase()) > -1;

  const urlTest = urlPattern.test(url);

  return httpTest && urlTest;
}

/**
 * Calls to update the keycloak token if the request should update the token.
 *
 * @param req http request from @angular http module.
 * @returns
 * A promise boolean for the token update or noop result.
 */
async function conditionallyUpdateToken(
  req: HttpRequest<unknown>,
  keycloak: KeycloakService,
): Promise<boolean> {
  if (keycloak.shouldUpdateToken(req)) {
    return await keycloak.updateToken();
  }

  return true;
}

/**
 * Adds the token of the current user to the Authorization header
 *
 * @param req
 * @param next
 */
function handleRequestWithTokenHeader(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  keycloak: KeycloakService,
) {
  return keycloak.addTokenToHeader(req.headers).pipe(
    mergeMap((headersWithBearer) => {
      const kcReq = req.clone({ headers: headersWithBearer });
      return next(kcReq).pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            if (keycloak.isTokenExpired?.()) keycloak.login?.();
          }
          throw err;
        }),
      );
    }),
  );
}
